import type { ReactNode } from "react";
import type {
	EntryShared,
	ModuleFormValues,
	OptionalSubmodule,
	StartWithWithoutTierTwo,
	T2Schema,
	TranslationKey,
} from "@typedefs/modulesInterfaces";
import { Form, useFormikContext, getIn, FieldArray } from "formik";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useContextAwareGetSubmoduleDefaults } from "@hooks/index";
import { useAppSelector, useCustomTranslation, usePermissions, useWarningMessageT2 } from "@app/hooks";
import { selectCurrentBuilder, selectShowTier2 } from "@features/builder/builderSlice";
import { useLocation } from "react-router-dom";
import TranslatableText from "@components/translations/TranslatableText";
import { handleDisableSww, handleElectricityDisable } from "./moduleUtils";
import { preventFormSubmit } from "./inputs/utils";
import { TranslatedFormSubmodule } from "@components/formSubmodule/TranslatedFormSubmodule";
import { MissingEmissionFactorForCountry } from "@utils/useMissingEmissionFactorForCountry";
import { isEqual } from "lodash";
import FormSubmodule from "@components/formSubmodule/FormSubmodule";
import GenericInputGroup from "@components/input/inputGroup/GenericInputGroup";
import DefaultsErrorNote from "@components/messageNote/DefaultsErrorNote";
import LoadDefaultsButton from "./LoadDefaultsButton";
import useErrorMessage from "@utils/useErrorMessage";
import FadeAnimation from "@components/animations/FadeAnimation";

type GenericT2Context = {
	isReadOnly: boolean;
}
const GenericTwoContext = createContext<GenericT2Context>({
	isReadOnly: false
});

const GenericTierTwo = ({
	title,
	t2Schema,
	warningMessage,
}: {
	title: TranslationKey;
	t2Schema: T2Schema;
	warningMessage?: string | null
}) => {
	const { values } = useFormikContext<ModuleFormValues>();

	const { isReadOnly } = usePermissions();
	const { warningMessages, handleDefaultsError } = useWarningMessageT2();
	const showTier2 = useAppSelector(selectShowTier2);
	const { moduleList } = useAppSelector(selectCurrentBuilder);
	const { pathname } = useLocation();

	// Resolved from the route rather than a prop so the header can match Tier 1's
	// without threading beModuleType through all 22 module components.
	const number = useMemo(() => {
		const modulePath = pathname.split("/").pop();
		return moduleList.find((step) => step.path === modulePath)?.uniqueId ?? "0.0";
	}, [pathname, moduleList]);

	if (!showTier2) return null;
	return (
		<GenericTwoContext.Provider value={{ isReadOnly }}>
			<section>
				<Form onKeyDown={preventFormSubmit}>
					<div className="module-group">
						<div className="pos-relative mb-2 pb-1">
							<h2 className="fs-14 ff-medium-ext module-header w-fit">
								<TranslatableText translationKey={title} />
							</h2>
							<div className="module-number header-number ff-light-ext">
								{number}
							</div>
						</div>

						{warningMessage || warningMessages.length ? (
							<div className="d-flex f-column">
								{warningMessage ? <DefaultsErrorNote noteMessage={warningMessage} /> : null}
								{warningMessages.map((warning) => (
									<DefaultsErrorNote key={warning.id} noteMessage={warning.message} />
								))}
							</div>
						) : null}

						<LoadDefaultsButton />

						{t2Schema.submodules.map((submodule) => {
							return (
								<FormSubmodule submoduleName={submodule.name} key={submodule.name}>
									<TierTwoBlock>
										{submodule.inputGroups.map((input, index) => {
											const curatedInput = handleDisableSww({ input, isReadOnly, index });
											return (
												<GenericInputGroup
													key={`submodule-${index}-${submodule.name}`}
													inputGroup={{ ...curatedInput, disabled: isReadOnly }}
												/>
											)
										})}

										{submodule.conditionalSections?.map((section) => (
											<FadeAnimation isShown={getIn(values, section.conditionName)}>
												<>
													{section.inputGroups.map((input, i) => (
														<div key={`${input.label}-${i}`} className="px-2">
															<GenericInputGroup inputGroup={{ ...input, disabled: isReadOnly }} />
														</div>
													))}
												</>
											</FadeAnimation>
										))}
									</TierTwoBlock>
								</FormSubmodule>
							)
						})}
						{t2Schema.optionalSubmodules?.map((submodule, index) => (
							<FormOptionalSubmodules
								submodule={submodule}
								key={`optsubmodule-${index}-${submodule.name}`}
								onDefaultsError={handleDefaultsError}
							/>
						))}
					</div>
				</Form>
			</section>
		</GenericTwoContext.Provider>
	);
};

const FormOptionalSubmodules = <T,>({ submodule, onDefaultsError }: { submodule: OptionalSubmodule<T>, onDefaultsError: (message: string) => void }) => {
	const { values } = useFormikContext<ModuleFormValues>();
	const { isReadOnly } = useContext(GenericTwoContext);
	const submoduleValues = getIn(values, submodule.inputName);

	const [list, setList] = useState<any[]>([]);

	useEffect(() => {
		const newList = getIn(values, submodule.inputName)
		const isListUpdated = !isEqual(list, newList)
		if (isListUpdated) {
			setList(newList)
		}
	}, [values, submodule.inputName]);

	return Array.isArray(submoduleValues) ? (
		<>
			<MissingEmissionFactorForCountry handleDefaultsError={onDefaultsError} listInputName={submodule.inputName} />
			<FieldArray name={submodule.inputName}>
				{() => (
					<>
						{list.map((el, index) => {
							return el.id ? (
								<FormOptionalSubmoduleELement
									entry={el}
									submodule={submodule}
									index={index}
									submoduleName={submodule.name}
									key={`${index + 1}. ${submodule.name}`}
									id={el.id}
									onDefaultsError={onDefaultsError}
								/>
							) : null;
						})}
					</>
				)}
			</FieldArray>
		</>
	) : (
		<FormSubmodule submoduleName={submodule.name}>
			<TierTwoBlock>
				{submodule.inputGroups.map((input, i) => {
					const curatedInput = handleDisableSww({ input, isReadOnly, index: i });
					return (
						<GenericInputGroup
							key={`${submodule.name}-${i}`}
							inputGroup={{ ...curatedInput, disabled: isReadOnly }}
						/>
					)
				})}
			</TierTwoBlock>
		</FormSubmodule>
	);
};

const FormOptionalSubmoduleELement = <T,>({
	submodule,
	entry,
	index,
	submoduleName,
	id,
	onDefaultsError
}: {
	submodule: OptionalSubmodule<T>;
	entry: EntryShared;
	index: number;
	submoduleName: TranslationKey;
	id: number;
	onDefaultsError?: (message: string) => void
}) => {
	const { isReadOnly } = useContext(GenericTwoContext);
	const { setFieldValue, values } = useFormikContext<ModuleFormValues>();
	const currentEntry = getIn(values, `${submodule.inputName}.${index}`) ?? entry;
	const conditionValues = (submodule.conditionalSections ?? []).map((section) =>
		Boolean(getIn(values, `${submodule.inputName}.${index}.${section.conditionName}`)),
	);
	const electricityFlags = {
		is_start: Boolean(getIn(values, `${submodule.inputName}.${index}.is_electricity_start`)),
		is_w: Boolean(getIn(values, `${submodule.inputName}.${index}.is_electricity_w`)),
		is_wo: Boolean(getIn(values, `${submodule.inputName}.${index}.is_electricity_wo`)),
	};
	const { data: defaults, isError, error } = useContextAwareGetSubmoduleDefaults({
		uniqueId: id,
		route: submodule.route ?? "",
		cacheTag: submodule.cacheTag ?? "FakeTag",
	},
		{ skip: !id || !submodule.route || !submodule.cacheTag },
	);

	//conditional section
	useEffect(() => {
		for (const section of submodule.conditionalSections ?? []) {
			const sectionVisible = Boolean(getIn(values, `${submodule.inputName}.${index}.${section.conditionName}`));
			if (section.initialValues && !sectionVisible) {
				const nextEntry = { ...currentEntry, ...section.initialValues };
				if (!isEqual(currentEntry, nextEntry)) {
					setFieldValue(`${submodule.inputName}.${index}`, nextEntry)
				}
			}
		}
	}, [submodule.conditionalSections, conditionValues.join("|"), setFieldValue, values, submodule.inputName, index])

	// Re-apply defaults when the entry is reset (e.g. enableReinitialize after server sync)
	// without re-running on every user edit to non-default fields.
	useEffect(() => {
		if (!defaults || !setFieldValue || !submodule.route || index === undefined) return;
		const entry = getIn(values, `${submodule.inputName}.${index}`);
		if (!entry?.id) return;

		const entryWithDefaults = { ...entry, ...defaults };
		const defaultFieldKeys = Object.keys(defaults).filter((k) => k.endsWith("_default"));
		const needsDefaults =
			defaultFieldKeys.length > 0
				? defaultFieldKeys.some((k) => !isEqual(entry[k], defaults[k]))
				: !isEqual(entry, entryWithDefaults);

		if (needsDefaults && !isEqual(entry, entryWithDefaults)) {
			setFieldValue(`${submodule.inputName}.${index}`, entryWithDefaults);
		}
	}, [defaults, setFieldValue, index, submodule.route, submodule.inputName, values]);

	const { errorMsg: defaultsErrorMsg } = useErrorMessage({ isError, error });
	useEffect(() => {
		if (defaultsErrorMsg && onDefaultsError) onDefaultsError(defaultsErrorMsg);
	}, [defaultsErrorMsg, onDefaultsError])


	return (
		<TranslatedFormSubmodule
			submoduleName={submoduleName}
			itemId={id}
			itemIndex={index + 1}
			className="pt-2"
		>
			<TierTwoBlock>
				{submodule.inputGroups.map((input, i) => {
					const curatedInput = handleDisableSww({ input, isReadOnly, index });
					const electricityHandledInput = handleElectricityDisable({
						input: curatedInput as StartWithWithoutTierTwo,
						electricityFlags,
						isReadOnly,
					});
					return (
						<GenericInputGroup
							key={`${submodule.name}-${i}`}
							inputGroup={{ ...electricityHandledInput, index, disabled: isReadOnly }}
						/>
					)
				})}

				{submodule.conditionalSections?.map((section) => (
					<FadeAnimation
						isShown={Boolean(getIn(values, `${submodule.inputName}.${index}.${section.conditionName}`))}
						key={`${section.conditionName}-${index}`}
					>
						<>
							{section.inputGroups.map((input) => {
								const curatedInput = handleDisableSww({ input, isReadOnly, index });
								const electricityHandledInput = handleElectricityDisable({
									input: curatedInput as StartWithWithoutTierTwo,
									electricityFlags,
									isReadOnly,
								});
								return (
									<GenericInputGroup
										key={`${input.label}-${entry.id}`}
										inputGroup={{ ...electricityHandledInput, disabled: isReadOnly, index }}
									/>
								);
							})}
						</>
					</FadeAnimation>
				))}
			</TierTwoBlock>
		</TranslatedFormSubmodule>
	);
};

export const TierTwoBlock = ({ children }: { children: ReactNode }) => {
	return <div className="tiertwo-block">{children}</div>;
};

export default GenericTierTwo;
